import { useEffect, useMemo, useRef, useState } from 'react';
import type { IReportAhuRow } from 'app/services/reportsBoardApi';
import { sectionFlagKey, type IGroupedAhu } from '../reportScopes';

/**
 * Bespoke AHU checkbox tree — port of main_header.tmpl:8-40 +
 * main_header.js toggleAhuMultiSelect/onAhuMultiAll/onAhuMultiRow/
 * _refreshAhuMultiSummary/_syncSelectedAhuIds (:560-668).
 *
 * Rows: master "All main AHUs" + one row per main AHU + indented version rows
 * labeled (static)/(editable). Server returns a FLAT list; the page regroups
 * via groupAhusByMain (main_header.js:35-58) and passes `ahusGrouped`.
 *
 * Defaults (template state, re-applied whenever the report/project/AHU list
 * changes — the legacy re-render resets the DOM; the panel does NOT reset on
 * mere close/re-open, because the legacy reset-on-open comment at
 * main_header.js:572-573 refers to _resetAhuMultiDefaults which is dead code):
 * master checked, every non-disabled main checked, versions unchecked.
 * Disabled = the current report is one of the four section reports and the
 * row lacks that section (main_header.tmpl:23-33).
 *
 * Summary text (:651-668): master checked → 'All main AHUs'; 0 picked →
 * 'None selected'; 1 → that row's tag text (versions include the
 * (static)/(editable) suffix); n → 'n AHUs selected'.
 *
 * Interactions do NOT touch the URL hash and do NOT re-gate the menu — only
 * selected ids for the empty-board/canGenerate live recompute (onChange).
 */

// Inline styles copied verbatim from main_header.tmpl:10-12 — they carry
// !important (beats `.header-buttons button` in report styles.css:359), which
// React style objects can't express, so they're applied as literal style
// attributes via refs.
const TOGGLE_STYLE =
  'background:none !important; color:#757575 !important; border:none !important; ' +
  'border-bottom:1px solid #ddd !important; -webkit-appearance:none; appearance:none;';
const SUMMARY_STYLE = 'color:#757575 !important; text-align:left !important;';
const CARET_STYLE = 'color:#757575 !important;';

interface AhuMultiSelectProps {
  ahusGrouped: IGroupedAhu[];
  /** Current report VALUE (drives the section-report disabled rows). */
  reportValue: string | null;
  onChange: (selectedIds: number[]) => void;
}

interface CheckState {
  all: boolean;
  rows: Record<number, boolean>;
}

const versionLabel = (v: IReportAhuRow) =>
  `${v.static_version_name ? v.static_version_name : v.tag} ${
    v.static_version ? '(static)' : '(editable)'
  }`;

const AhuMultiSelect = ({ ahusGrouped, reportValue, onChange }: AhuMultiSelectProps) => {
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement | null>(null);

  const flagKey = sectionFlagKey(reportValue);
  const isDisabled = (row: IReportAhuRow) => !!flagKey && !row.reports?.[flagKey];

  const defaults = useMemo<CheckState>(() => {
    const rows: Record<number, boolean> = {};
    ahusGrouped.forEach((m) => {
      rows[m.id] = !isDisabled(m); // mains checked unless disabled (tmpl:25)
      m.versions.forEach((v) => {
        rows[v.id] = false; // versions never default-checked (tmpl:32)
      });
    });
    return { all: true, rows };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ahusGrouped, flagKey]);

  const [state, setState] = useState<CheckState>(defaults);

  // Legacy re-render reset: report/project/AHU-list change re-templates the
  // panel back to defaults.
  useEffect(() => {
    setState(defaults);
    setOpen(false);
  }, [defaults]);

  // Close on outside click (capture) — toggleAhuMultiSelect :577-589.
  useEffect(() => {
    if (!open) return undefined;
    const onDoc = (ev: MouseEvent) => {
      if (rootRef.current && !rootRef.current.contains(ev.target as Node)) {
        setOpen(false);
      }
    };
    const t = window.setTimeout(() => {
      document.addEventListener('click', onDoc, true);
    }, 0);
    return () => {
      window.clearTimeout(t);
      document.removeEventListener('click', onDoc, true);
    };
  }, [open]);

  const sync = (next: CheckState) => {
    setState(next);
    // _syncSelectedAhuIds (:633-638): checked, non-disabled mains + versions.
    const ids: number[] = [];
    ahusGrouped.forEach((m) => {
      if (next.rows[m.id] && !isDisabled(m)) ids.push(m.id);
      m.versions.forEach((v) => {
        if (next.rows[v.id] && !isDisabled(v)) ids.push(v.id);
      });
    });
    onChange(ids);
  };

  const onToggleClick = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (open) {
      setOpen(false);
      return;
    }
    // Do NOT reset here: despite the legacy comment at main_header.js:572-573,
    // _resetAhuMultiDefaults (:592-600) is dead code — nothing calls it — so
    // the LIVE board keeps the user's picks across close/re-open (verified on
    // dev3 2026-07-11). State only resets on the legacy re-render triggers
    // (report/project/AHU-list change), handled by the [defaults] effect above.
    setOpen(true);
  };

  // onAhuMultiAll (:602-614): master drives every non-disabled main; any
  // individually-picked versions are cleared.
  const onAll = (checked: boolean) => {
    const rows: Record<number, boolean> = {};
    ahusGrouped.forEach((m) => {
      rows[m.id] = checked && !isDisabled(m);
      m.versions.forEach((v) => {
        rows[v.id] = false;
      });
    });
    sync({ all: checked, rows });
  };

  // onAhuMultiRow (:616-631): master stays checked iff every non-disabled
  // main is checked AND no version is checked.
  const onRow = (id: number, checked: boolean) => {
    const rows = { ...state.rows, [id]: checked };
    const mains = ahusGrouped.filter((m) => !isDisabled(m));
    const allMainsChecked = mains.length > 0 && mains.every((m) => rows[m.id]);
    const anyVersion = ahusGrouped.some((m) => m.versions.some((v) => rows[v.id]));
    sync({ all: allMainsChecked && !anyVersion, rows });
  };

  // _refreshAhuMultiSummary (:651-668).
  const summary = useMemo(() => {
    if (state.all) return 'All main AHUs';
    const picked: string[] = [];
    ahusGrouped.forEach((m) => {
      if (state.rows[m.id]) picked.push(m.tag);
      m.versions.forEach((v) => {
        if (state.rows[v.id]) picked.push(versionLabel(v));
      });
    });
    if (picked.length === 0) return 'None selected';
    if (picked.length === 1) return picked[0];
    return `${picked.length} AHUs selected`;
  }, [state, ahusGrouped]);

  return (
    <div className="ahu-multi-select" id="ahu_multiselect" ref={rootRef}>
      <button
        type="button"
        className="ahu-multi-toggle"
        id="ahu_multi_toggle"
        aria-haspopup="listbox"
        aria-expanded={open ? 'true' : 'false'}
        onClick={onToggleClick}
        ref={(el) => el?.setAttribute('style', TOGGLE_STYLE)}
      >
        <span
          className="ahu-multi-summary"
          ref={(el) => el?.setAttribute('style', SUMMARY_STYLE)}
        >
          {summary}
        </span>
        <span
          className="ahu-multi-caret"
          ref={(el) => el?.setAttribute('style', CARET_STYLE)}
        >
          &#9662;
        </span>
      </button>
      <div className="ahu-multi-panel" id="ahu_multi_panel" role="listbox" hidden={!open}>
        <ul className="ahu-multi-list">
          <li className="ahu-multi-row ahu-multi-all" data-id="all">
            {/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
            <label>
              <input
                type="checkbox"
                className="ahu-multi-cb ahu-multi-cb-all"
                checked={state.all}
                onChange={(e) => onAll(e.target.checked)}
              />
              <span className="ahu-multi-tag">All main AHUs</span>
            </label>
          </li>
          {ahusGrouped.map((m) => {
            const mainDisabled = isDisabled(m);
            return [
              <li
                key={`m-${m.id}`}
                className={`ahu-multi-row ahu-multi-main${mainDisabled ? ' ahu-multi-disabled' : ''}`}
                data-id={m.id}
              >
                {/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
                <label>
                  <input
                    type="checkbox"
                    className="ahu-multi-cb ahu-multi-cb-main"
                    data-id={m.id}
                    checked={!!state.rows[m.id]}
                    disabled={mainDisabled}
                    onChange={(e) => onRow(m.id, e.target.checked)}
                  />
                  <span className="ahu-multi-tag">{m.tag}</span>
                </label>
              </li>,
              ...m.versions.map((v) => {
                const vDisabled = isDisabled(v);
                return (
                  <li
                    key={`v-${v.id}`}
                    className={`ahu-multi-row ahu-multi-version${vDisabled ? ' ahu-multi-disabled' : ''}`}
                    data-id={v.id}
                    data-parent-id={v.parent_version_id ?? undefined}
                  >
                    {/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
                    <label>
                      <input
                        type="checkbox"
                        className="ahu-multi-cb ahu-multi-cb-version"
                        data-id={v.id}
                        data-parent-id={v.parent_version_id ?? undefined}
                        checked={!!state.rows[v.id]}
                        disabled={vDisabled}
                        onChange={(e) => onRow(v.id, e.target.checked)}
                      />
                      <span className={`ahu-multi-tag${v.static_version ? ' is-static' : ''}`}>
                        {v.static_version_name ? v.static_version_name : v.tag}{' '}
                        <em>{v.static_version ? '(static)' : '(editable)'}</em>
                      </span>
                    </label>
                  </li>
                );
              }),
            ];
          })}
        </ul>
      </div>
    </div>
  );
};

export default AhuMultiSelect;
